import { useState } from "react";
import { X, Key, Save } from "lucide-react";
import { User } from "../../shared/users/UserDetails";

interface UpdatePromoCodeModalProps {
  user: User;
  currentCode: string;
  onClose: () => void;
  onUpdate: (newCode: string) => Promise<void>;
}

export default function UpdatePromoCodeModal({ user, currentCode, onClose, onUpdate }: UpdatePromoCodeModalProps) {
  const [newCode, setNewCode] = useState(currentCode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const trimmedCode = newCode.trim();

    if (!trimmedCode) {
      setError("Le code promo ne peut pas être vide");
      return;
    }

    if (trimmedCode === currentCode) {
      setError("Le code est identique à l'actuel");
      return;
    }

    setLoading(true);
    try {
      await onUpdate(trimmedCode);
      onClose();
    } catch (err: any) {
      setError(err.message || "Erreur lors de la mise à jour du code promo");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center space-x-2">
            <Key className="w-5 h-5 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900">Modifier le code promo</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          {/* Informations utilisateur */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <span className="text-gray-500">Utilisateur:</span>
              <span className="font-medium text-gray-900">{user.email || user.name}</span>
            </div>
          </div>

          {/* Code actuel */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              Code actuel :<span className="font-bold ml-1">{currentCode}</span>
            </p>
          </div>

          {/* Nouveau code */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Nouveau code promo</label>
            <input
              type="text"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Entrez le nouveau code promo"
              required
              autoFocus
            />
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {loading ? "Mise à jour..." : "Modifier"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
